/* ==========================================================================
   nb-cart.js — Nuttet Baby kurv (AJAX Cart API + drawer)

   Erstatter prototypens design/cart.js (localStorage) 1:1 i adfærd:
     addToCart(item)   → POST /cart/add.js      → åbner draweren
     setQty(id, qty)   → POST /cart/change.js   {line, quantity}
     removeFromCart    → POST /cart/change.js   {line, quantity: 0}
     getCart()         → GET  /cart.js
     nb-cart-updated   → bevaret, dispatches på window med kurven i detail
     nb-cart-open      → bevaret, åbner draweren

   Efter hver ændring hentes ny HTML via Section Rendering API
   (?sections=cart-drawer,header). Der bygges ALDRIG kurv-markup i JS —
   eneste undtagelse er upsell-kortene, som kommer fra
   /recommendations/products.json og derfor ikke kan renderes af Liquid.

   INGEN dansk tekst i denne fil. Alle strenge kommer fra data-attributter
   sat i sections/cart-drawer.liquid, snippets/cart-line.liquid og
   snippets/product-card.liquid.

   Alt er delegeret fra document og idempotent, fordi Section Rendering API
   udskifter DOM under fødderne på os.
   ========================================================================== */

(function () {
  'use strict';

  if (window.NBCart && window.NBCart.__ready) return;

  /* ------------------------------------------------------------------------
     Konstanter
     ---------------------------------------------------------------------- */
  var ADDED_FEEDBACK_MS = 1200; // "Tilføjet ✓" står ~1,2s før draweren åbner
  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /* ------------------------------------------------------------------------
     Små hjælpere
     ---------------------------------------------------------------------- */
  function root() {
    return document.querySelector('[data-nb-cart-drawer-root]');
  }

  function drawerEl() {
    return document.querySelector('[data-nb-cart-drawer]');
  }

  function cfg(name, fallback) {
    var el = root();
    if (!el) return fallback;
    var value = el.getAttribute('data-nb-' + name);
    return value === null || value === '' ? fallback : value;
  }

  function closest(el, selector) {
    if (!el || !el.closest) return null;
    return el.closest(selector);
  }

  function sectionIdOf(el) {
    var wrapper = closest(el, '[id^="shopify-section-"]');
    if (!wrapper) return null;
    return wrapper.id.replace('shopify-section-', '');
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!response.ok) {
            var error = new Error('nb-cart');
            error.data = data;
            throw error;
          }
          return data;
        });
    });
  }

  function getCart() {
    return fetch(cfg('cart-url', '/cart') + '.js', {
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      return response.json();
    });
  }

  /* Shopifys egen fejlbeskrivelse foretrækkes — den er allerede oversat.
     Ellers falder vi tilbage på temaets locale-nøgle fra data-attributten. */
  function errorText(error) {
    var data = error && error.data;
    if (data && typeof data.description === 'string' && data.description) return data.description;
    if (data && typeof data.message === 'string' && data.message) return data.message;
    return cfg('i18n-error', '');
  }

  /* ------------------------------------------------------------------------
     Pengeformat — kun til upsell-kortene, hvor priserne kommer som ører
     fra JSON-API'et. Formatstrengen kommer fra shop.money_format i Liquid.
     ---------------------------------------------------------------------- */
  function formatMoney(cents, format) {
    var formatString = format || '{{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = formatString.match(placeholder);
    if (!match) return formatString;

    var amount = typeof cents === 'string' ? parseInt(cents.replace(/[^0-9-]/g, ''), 10) : cents;
    if (isNaN(amount) || amount === null) amount = 0;

    function withDelimiters(precision, thousands, decimal) {
      var number = (amount / 100.0).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var rest = parts[1] ? decimal + parts[1] : '';
      return whole + rest;
    }

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(0, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(2, ' ', '.');
        break;
      default:
        value = withDelimiters(2, ',', '.');
    }
    return formatString.replace(placeholder, value);
  }

  function imageWithWidth(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* ------------------------------------------------------------------------
     Fokusfælde. Bruger window.NBModal hvis den findes (nb-menu.js /
     nb-menu.js ejer den fælles implementering) — ellers vores egen, som
     samtidig udstilles som NBModal, så begge sider deler samme kontrakt:
       NBModal.trap(element) → returnerer en release-funktion.
     ---------------------------------------------------------------------- */
  function nbTrap(container) {
    function onKeydown(event) {
      if (event.key !== 'Tab' || !container) return;

      var items = Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      });
      if (!items.length) {
        event.preventDefault();
        container.focus();
        return;
      }

      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;

      if (!container.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || active === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeydown, true);
    return function release() {
      document.removeEventListener('keydown', onKeydown, true);
    };
  }

  if (!window.NBModal) window.NBModal = { trap: nbTrap };

  /* ------------------------------------------------------------------------
     Åbn / luk
     Tilstanden ligger på <body>, så draweren overlever at blive re-renderet.
     ---------------------------------------------------------------------- */
  var isOpen = false;
  var releaseTrap = null;
  var lastFocused = null;

  function focusInsideDrawer() {
    var drawer = drawerEl();
    if (!drawer) return;
    var target = drawer.querySelector('[data-nb-cart-close]') || drawer;
    try {
      target.focus({ preventScroll: true });
    } catch (e) {
      target.focus();
    }
  }

  function openDrawer() {
    var drawer = drawerEl();
    if (!drawer || isOpen) return;

    lastFocused = document.activeElement;
    isOpen = true;
    document.body.classList.add('nb-cart-is-open');

    var modal = window.NBModal;
    if (modal && typeof modal.trap === 'function') {
      try {
        releaseTrap = modal.trap(drawer) || null;
      } catch (e) {
        releaseTrap = nbTrap(drawer);
      }
    } else {
      releaseTrap = nbTrap(drawer);
    }

    window.setTimeout(focusInsideDrawer, 80);
    loadUpsell();
  }

  function closeDrawer() {
    if (!isOpen) return;
    isOpen = false;
    document.body.classList.remove('nb-cart-is-open');

    if (typeof releaseTrap === 'function') {
      try {
        releaseTrap();
      } catch (e) {
        /* ingen handling — fælden er væk uanset */
      }
    }
    releaseTrap = null;
    hideDrawerError();

    if (lastFocused && document.contains(lastFocused)) {
      try {
        lastFocused.focus({ preventScroll: true });
      } catch (e) {
        lastFocused.focus();
      }
    }
    lastFocused = null;
  }

  /* ------------------------------------------------------------------------
     Fejlbeskeder — altid inline, aldrig alert()
     ---------------------------------------------------------------------- */
  function showDrawerError(message) {
    var el = document.querySelector('[data-nb-cart-error]');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
  }

  function hideDrawerError() {
    var el = document.querySelector('[data-nb-cart-error]');
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  function showLineError(lineEl, message) {
    if (!lineEl) return;
    var el = lineEl.querySelector('[data-nb-line-error]');
    if (!el || !message) {
      showDrawerError(message);
      return;
    }
    el.textContent = message;
    el.hidden = false;
  }

  function hideLineError(lineEl) {
    if (!lineEl) return;
    var el = lineEl.querySelector('[data-nb-line-error]');
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  /* ------------------------------------------------------------------------
     Badge — antal STYK, ikke antal linjer
     ---------------------------------------------------------------------- */
  function setBadge(count) {
    var badges = document.querySelectorAll('[data-nb-cart-count]');
    Array.prototype.forEach.call(badges, function (badge) {
      badge.textContent = String(count);
      if (count > 0) badge.removeAttribute('hidden');
      else badge.setAttribute('hidden', '');
    });
  }

  /* ------------------------------------------------------------------------
     Section Rendering API
     Draweren (og en evt. kurv-side-sektion mærket data-nb-cart-section)
     erstattes helt. Headeren røres ikke — kun dens badge synkroniseres, så
     åbne mega-menuer og søgefeltets tilstand ikke rives ned.
     ---------------------------------------------------------------------- */
  function renderTargets() {
    var targets = [];

    function add(el, mode) {
      var id = sectionIdOf(el);
      if (!id) return;
      var existing = null;
      for (var i = 0; i < targets.length; i++) {
        if (targets[i].id === id) existing = targets[i];
      }
      if (existing) {
        if (mode === 'replace') existing.mode = 'replace';
        return;
      }
      targets.push({ id: id, mode: mode, el: document.getElementById('shopify-section-' + id) });
    }

    add(root(), 'replace');
    add(document.querySelector('[data-nb-cart-count]'), 'badge');
    Array.prototype.forEach.call(document.querySelectorAll('[data-nb-cart-section]'), function (el) {
      add(el, 'replace');
    });

    return targets.filter(function (target) {
      return target.el;
    });
  }

  function sectionIds() {
    return renderTargets().map(function (target) {
      return target.id;
    });
  }

  /* Payload til /cart/add.js og /cart/change.js: beder Shopify rendere
     sektionerne i SAMME svar, så draweren kan opdateres uden ekstra kald. */
  function sectionsPayload() {
    var ids = sectionIds();
    if (!ids.length) return {};
    return { sections: ids.join(','), sections_url: cfg('root-url', '/') };
  }

  function finishRender() {
    // Udskiftet DOM kan have taget fokus med sig ud af draweren.
    var drawer = drawerEl();
    if (isOpen && drawer && !drawer.contains(document.activeElement)) focusInsideDrawer();
    loadUpsell();
  }

  /* Indsætter server-renderet sektions-HTML ({id: html}). Returnerer true,
     hvis mindst ét mål blev opdateret. */
  function applySections(map) {
    if (!map) return false;

    var parser = new DOMParser();
    var applied = false;

    renderTargets().forEach(function (target) {
      var html = map[target.id];
      if (typeof html !== 'string') return;

      var fresh = parser.parseFromString(html, 'text/html');
      var wrapper = fresh.querySelector('[id^="shopify-section-"]') || fresh.body;
      if (!wrapper) return;

      if (target.mode === 'badge') {
        var freshBadge = wrapper.querySelector('[data-nb-cart-count]');
        var liveBadge = target.el.querySelector('[data-nb-cart-count]');
        if (freshBadge && liveBadge) {
          liveBadge.textContent = freshBadge.textContent.trim();
          if (freshBadge.hasAttribute('hidden')) liveBadge.setAttribute('hidden', '');
          else liveBadge.removeAttribute('hidden');
        }
        applied = true;
        return;
      }

      target.el.innerHTML = wrapper.innerHTML;
      applied = true;
    });

    if (applied) finishRender();
    return applied;
  }

  function refreshSections() {
    var ids = sectionIds();
    if (!ids.length) return Promise.resolve();

    var base = cfg('root-url', '/');
    var url;
    try {
      url = new URL(base, window.location.origin);
    } catch (e) {
      return Promise.resolve();
    }
    url.searchParams.set('sections', ids.join(','));

    return fetch(url.toString(), { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        applySections(data);
      })
      .catch(function () {
        /* Netværksfejl: kurven er allerede opdateret på serveren.
           Vi lader UI'et stå og viser fejlen der hvor handlingen skete. */
      });
  }

  function afterCartChange(cart) {
    if (cart && typeof cart.item_count === 'number') setBadge(cart.item_count);
    window.dispatchEvent(new CustomEvent('nb-cart-updated', { detail: { cart: cart || null } }));
    // /cart/change.js leverer sektionerne i svaret — så er ét kald nok.
    if (cart && cart.sections && applySections(cart.sections)) return Promise.resolve();
    return refreshSections();
  }

  /* ------------------------------------------------------------------------
     Cart API
     ---------------------------------------------------------------------- */
  function addToCart(variantId, quantity, properties) {
    var item = { id: variantId, quantity: quantity || 1 };
    if (properties) item.properties = properties;
    var body = sectionsPayload();
    body.items = [item];
    return postJSON(cfg('add-url', '/cart/add') + '.js', body);
  }

  function changeLine(lineNumber, quantity) {
    var body = sectionsPayload();
    body.line = lineNumber;
    body.quantity = quantity;
    return postJSON(cfg('change-url', '/cart/change') + '.js', body);
  }

  /* Optimistisk UI: feltet opdateres med det samme og rulles tilbage,
     hvis serveren siger nej. */
  function updateLine(lineEl, quantity) {
    if (!lineEl || lineEl.classList.contains('is-busy')) return;

    var lineNumber = parseInt(lineEl.getAttribute('data-nb-line'), 10);
    if (isNaN(lineNumber)) return;

    var input = lineEl.querySelector('[data-nb-qty-input]');
    var previous = lineEl.getAttribute('data-nb-quantity') || (input ? input.value : '1');

    if (quantity < 0) quantity = 0;

    hideLineError(lineEl);
    hideDrawerError();
    lineEl.classList.add('is-busy');
    if (input) input.value = String(quantity);

    changeLine(lineNumber, quantity)
      .then(function (cart) {
        return afterCartChange(cart);
      })
      .then(function () {
        // Linjen er normalt erstattet af frisk HTML her — men fjern altid
        // låsen, så en fejlet sektions-hentning ikke kan fryse stepperen.
        lineEl.classList.remove('is-busy');
      })
      .catch(function (error) {
        lineEl.classList.remove('is-busy');
        if (input) input.value = previous;
        showLineError(lineEl, errorText(error));
      });
  }

  /* ------------------------------------------------------------------------
     Tilføj-til-kurv fra produktkort og upsell
     Labels kommer fra data-attributter; mangler de (ikon-knappen i
     upsell'en), skiftes kun data-nb-state.
     ---------------------------------------------------------------------- */
  function quickAdd(button) {
    if (!button || button.getAttribute('data-nb-state') === 'adding') return;

    var variantId = button.getAttribute('data-nb-quickadd');
    if (!variantId) return;

    var labelDefault = button.getAttribute('data-nb-label-default');
    var labelAdding = button.getAttribute('data-nb-label-adding');
    var labelAdded = button.getAttribute('data-nb-label-added');
    var hasLabels = !!(labelDefault && labelAdding && labelAdded);

    button.setAttribute('data-nb-state', 'adding');
    button.disabled = true;
    if (hasLabels) button.textContent = labelAdding;

    // Åbn draweren MED DET SAMME (optimistisk) — indholdet fyldes ind bagefter.
    window.dispatchEvent(new CustomEvent('nb-cart-open'));

    addToCart(variantId, 1)
      .then(function (data) {
        button.setAttribute('data-nb-state', 'added');
        if (hasLabels) button.textContent = labelAdded;

        // /cart/add.js leverer sektionerne i svaret — draweren fyldes straks.
        var instant = data ? applySections(data.sections) : false;

        // Kurv-JSON hentes i baggrunden til badge + nb-cart-updated; uden
        // sections i svaret bruges fuld genopfriskning som før.
        getCart().then(function (cart) {
          if (!instant) return afterCartChange(cart);
          if (cart && typeof cart.item_count === 'number') setBadge(cart.item_count);
          window.dispatchEvent(new CustomEvent('nb-cart-updated', { detail: { cart: cart || null } }));
          return null;
        });

        // "Tilføjet ✓" nulstilles for sig; det blokerer ikke draweren.
        window.setTimeout(function () {
          if (document.contains(button)) {
            button.disabled = false;
            button.removeAttribute('data-nb-state');
            if (hasLabels) button.textContent = labelDefault;
          }
        }, ADDED_FEEDBACK_MS);
      })
      .catch(function (error) {
        button.disabled = false;
        button.removeAttribute('data-nb-state');
        if (hasLabels) button.textContent = labelDefault;
        showDrawerError(errorText(error));
        openDrawer();
      });
  }

  /* ------------------------------------------------------------------------
     Upsell — "Måske også til dig"
     Kandidater fra Shopifys anbefalinger; alt der allerede ligger i kurven
     filtreres fra (samme regel som prototypens related.js). Max 3.
     Ingen fallback: er der intet at foreslå, vises sektionen ikke.
     ---------------------------------------------------------------------- */
  var upsellToken = 0;

  function buildUpsellCard(product, host) {
    var addLabelTemplate = host.getAttribute('data-nb-i18n-add') || '';

    var card = document.createElement('div');
    card.className = 'nb-upsell';

    var media = document.createElement('a');
    media.className = 'nb-upsell__media';
    media.href = product.url;
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    if (product.featured_image) {
      var image = document.createElement('img');
      image.className = 'nb-upsell__img';
      image.src = imageWithWidth(product.featured_image, 92);
      image.srcset =
        imageWithWidth(product.featured_image, 46) + ' 1x, ' +
        imageWithWidth(product.featured_image, 92) + ' 2x, ' +
        imageWithWidth(product.featured_image, 138) + ' 3x';
      image.alt = '';
      image.width = 46;
      image.height = 46;
      image.loading = 'lazy';
      image.decoding = 'async';
      media.appendChild(image);
    }
    card.appendChild(media);

    var body = document.createElement('div');
    body.className = 'nb-upsell__body';

    var title = document.createElement('a');
    title.className = 'nb-upsell__title';
    title.href = product.url;
    title.textContent = product.title;
    body.appendChild(title);

    var prices = document.createElement('span');
    prices.className = 'nb-upsell__prices';

    var onSale = product.compare_at_price && product.compare_at_price > product.price;
    if (onSale) {
      var was = document.createElement('s');
      was.className = 'nb-upsell__was';
      was.textContent = formatMoney(product.compare_at_price, cfg('money-format', '{{amount}}'));
      prices.appendChild(was);
    }

    var price = document.createElement('span');
    price.className = 'nb-upsell__price' + (onSale ? ' is-sale' : '');
    price.textContent = formatMoney(product.price, cfg('money-format', '{{amount}}'));
    prices.appendChild(price);

    body.appendChild(prices);

    card.appendChild(body);

    /* Vælg den tilgængelige variant der matcher kurvens option-værdier
       bedst (fx rammestørrelse = plakatstørrelse). Uden match: første
       tilgængelige, som før. */
    var wanted = {};
    (host.getAttribute('data-nb-cart-options') || '').split('|').forEach(function (value) {
      value = value.trim().toLowerCase();
      if (value) wanted[value] = true;
    });

    var variant = null;
    var best = -1;
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      var candidate = variants[i];
      if (!candidate.available) continue;
      var score = 0;
      [candidate.option1, candidate.option2, candidate.option3].forEach(function (option) {
        if (option && wanted[String(option).trim().toLowerCase()]) score++;
      });
      if (score > best) {
        best = score;
        variant = candidate;
      }
    }

    if (variant) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'nb-upsell__add';
      button.setAttribute('data-nb-quickadd', variant.id);
      button.setAttribute('aria-label', addLabelTemplate.replace('[title]', product.title));
      var glyph = document.createElement('span');
      glyph.setAttribute('aria-hidden', 'true');
      glyph.textContent = '+';
      button.appendChild(glyph);
      card.appendChild(button);
    }

    return card;
  }

  function loadUpsell() {
    var host = document.querySelector('[data-nb-cart-upsell]');
    if (!host) return;

    var list = host.querySelector('[data-nb-cart-upsell-list]');
    var seed = host.getAttribute('data-nb-seed');
    if (!list || !seed) return;

    var limit = parseInt(host.getAttribute('data-nb-limit'), 10);
    if (isNaN(limit) || limit < 1) limit = 3;

    var inCart = (host.getAttribute('data-nb-in-cart') || '').split(',').filter(Boolean);

    var token = ++upsellToken;
    var url =
      cfg('recommend-url', '/recommendations/products') +
      '.json?product_id=' + encodeURIComponent(seed) +
      '&intent=complementary&limit=10';

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (token !== upsellToken) return;

        var current = document.querySelector('[data-nb-cart-upsell]');
        if (!current) return;
        var currentList = current.querySelector('[data-nb-cart-upsell-list]');
        if (!currentList) return;

        var products = (data && data.products) || [];
        var picked = products
          .filter(function (product) {
            return product && product.available && inCart.indexOf(String(product.id)) === -1;
          })
          .slice(0, limit);

        currentList.textContent = '';
        if (!picked.length) {
          current.hidden = true;
          return;
        }
        picked.forEach(function (product) {
          currentList.appendChild(buildUpsellCard(product, current));
        });
        current.hidden = false;
      })
      .catch(function () {
        var current = document.querySelector('[data-nb-cart-upsell]');
        if (current) current.hidden = true;
      });
  }

  /* ------------------------------------------------------------------------
     Delegerede lyttere — bindes én gang på document
     ---------------------------------------------------------------------- */
  function onClick(event) {
    if (event.defaultPrevented) return;

    var target = event.target;

    // Overlay
    if (closest(target, '[data-nb-cart-overlay]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    // Luk-knap (og tom-kurv-CTA, der samtidig navigerer videre)
    var closer = closest(target, '[data-nb-cart-close]');
    if (closer) {
      if (closer.tagName === 'A' && closer.getAttribute('href')) {
        closeDrawer();
        return;
      }
      event.preventDefault();
      closeDrawer();
      return;
    }

    // Åbn kurv fra headeren. Modifier-klik skal stadig kunne åbne /cart.
    var opener = closest(target, '[data-nb-cart-open]');
    if (opener) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      if (!drawerEl()) return;
      event.preventDefault();
      openDrawer();
      return;
    }

    // Antal-stepper
    var stepper = closest(target, '[data-nb-qty]');
    if (stepper) {
      event.preventDefault();
      var lineEl = closest(stepper, '[data-nb-cart-line]');
      if (!lineEl) return;
      var input = lineEl.querySelector('[data-nb-qty-input]');
      var current = parseInt(input ? input.value : lineEl.getAttribute('data-nb-quantity'), 10);
      if (isNaN(current)) current = parseInt(lineEl.getAttribute('data-nb-quantity'), 10) || 0;
      var step = parseInt(stepper.getAttribute('data-nb-qty'), 10) || 0;
      updateLine(lineEl, current + step);
      return;
    }

    // Fjern linje (0 = fjern)
    var remover = closest(target, '[data-nb-cart-remove]');
    if (remover) {
      event.preventDefault();
      updateLine(closest(remover, '[data-nb-cart-line]'), 0);
      return;
    }

    // Hurtig-tilføj fra produktkort og upsell
    var adder = closest(target, '[data-nb-quickadd]');
    if (adder) {
      event.preventDefault();
      quickAdd(adder);
    }
  }

  /* Linjens egenskaber, som Liquid har lagt i data-nb-properties. De SKAL
     følge med, når en linje skiftes til en anden variant — ellers taber vi
     fx "Til plakat", og pakkeriet kan ikke se hvilken plakat rammen hører
     til. Ugyldig JSON behandles som ingen egenskaber. */
  function linePropertiesOf(lineEl) {
    if (!lineEl) return null;
    var raw = lineEl.getAttribute('data-nb-properties');
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return Object.keys(parsed).length ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  /* Variant-skift på en kurvlinje: læg den nye variant i kurven (samme
     antal og samme egenskaber — add.js merger, hvis den allerede findes) og
     fjern derefter den gamle linje via dens stabile key. Sections-payload på
     sidste kald giver instant re-render. */
  function changeLineVariant(select) {
    var lineEl = closest(select, '[data-nb-cart-line]');
    if (!lineEl || lineEl.classList.contains('is-busy')) return;

    var key = lineEl.getAttribute('data-nb-key');
    var currentId = select.getAttribute('data-nb-current');
    var newId = select.value;
    if (!key || !newId || String(newId) === String(currentId)) return;

    var quantity = parseInt(lineEl.getAttribute('data-nb-quantity'), 10) || 1;
    var input = lineEl.querySelector('[data-nb-qty-input]');
    if (input) {
      var typed = parseInt(input.value, 10);
      if (!isNaN(typed) && typed > 0) quantity = typed;
    }

    var item = { id: newId, quantity: quantity };
    var properties = linePropertiesOf(lineEl);
    if (properties) item.properties = properties;

    hideLineError(lineEl);
    hideDrawerError();
    lineEl.classList.add('is-busy');

    postJSON(cfg('add-url', '/cart/add') + '.js', { items: [item] })
      .then(function () {
        var body = sectionsPayload();
        body.id = key;
        body.quantity = 0;
        return postJSON(cfg('change-url', '/cart/change') + '.js', body);
      })
      .then(function (cart) {
        return afterCartChange(cart);
      })
      .then(function () {
        lineEl.classList.remove('is-busy');
      })
      .catch(function (error) {
        lineEl.classList.remove('is-busy');
        if (currentId) select.value = currentId;
        showLineError(lineEl, errorText(error));
        refreshSections();
      });
  }

  function onChange(event) {
    var variantSelect = closest(event.target, '[data-nb-line-variant]');
    if (variantSelect) {
      changeLineVariant(variantSelect);
      return;
    }
    var input = closest(event.target, '[data-nb-qty-input]');
    if (!input) return;
    var lineEl = closest(input, '[data-nb-cart-line]');
    if (!lineEl) return;
    var quantity = parseInt(input.value, 10);
    if (isNaN(quantity) || quantity < 0) quantity = 0;
    updateLine(lineEl, quantity);
  }

  function onKeydown(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    if (!isOpen) return;
    closeDrawer();
  }

  document.addEventListener('click', onClick);
  document.addEventListener('change', onChange);
  document.addEventListener('keydown', onKeydown);

  window.addEventListener('nb-cart-open', function () {
    openDrawer();
  });

  // Theme editor: sektionen kan blive re-renderet under os.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('[data-nb-cart-drawer-root]')) loadUpsell();
  });

  /* ------------------------------------------------------------------------
     Offentligt API — samme navne som prototypens cart.js, så anden kode kan
     kalde det uden at kende Section Rendering-detaljerne.
     ---------------------------------------------------------------------- */
  window.NBCart = {
    __ready: true,
    open: openDrawer,
    close: closeDrawer,
    get: getCart,
    add: function (variantId, quantity, properties) {
      return addToCart(variantId, quantity, properties)
        .then(function (data) {
          var instant = data ? applySections(data.sections) : false;
          return getCart().then(function (cart) {
            if (!instant) {
              afterCartChange(cart);
            } else {
              if (cart && typeof cart.item_count === 'number') setBadge(cart.item_count);
              window.dispatchEvent(new CustomEvent('nb-cart-updated', { detail: { cart: cart || null } }));
            }
            window.dispatchEvent(new CustomEvent('nb-cart-open'));
            return cart;
          });
        });
    },
    setQuantity: function (lineNumber, quantity) {
      return changeLine(lineNumber, quantity).then(afterCartChange);
    },
    refresh: refreshSections,
    apply: applySections,
    sectionsPayload: sectionsPayload,
    formatMoney: formatMoney
  };
})();